import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { People, Pageview, TrendingUp, AccessTime } from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import DataTable from "../Requirement/components/common/ReusableTable";
import { fetchData } from "../../services/BackendServices";
import dayjs from "dayjs";

const AmtplAdminAnalytics = () => {
  // State for user tracking data (logged-in users)
  const [allTrackingData, setAllTrackingData] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniquePages, setUniquePages] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for non-logged-in users (external API)
  const [guestTrackingData, setGuestTrackingData] = useState([]);
  const [loadingGuest, setLoadingGuest] = useState(true);
  const [guestPage, setGuestPage] = useState(1);
  const [guestTotal, setGuestTotal] = useState(0);

  // Pagination state for full tracking table
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch user tracking data for last 3 days
  useEffect(() => {
    const fetchUserTrackingData = async () => {
      try {
        setLoading(true);

        // Calculate date 3 days ago
        const threeDaysAgo = dayjs().subtract(3, "day").format("YYYY-MM-DD");

        // Fetch all user tracking data from last 3 days
        const response = await fetchData({
          target: "listUserTracker",
          filterTerms: {},
          userOrder: "CreatedAt DESC",
          rowStart: 0,
          pageSize: 1000, // Get all records from last 3 days
        });

        if (response && response.items) {
          // Filter data for last 3 days
          const filteredData = response.items.filter((item) => {
            const itemDate = dayjs(item.CreatedAt).format("YYYY-MM-DD");
            return itemDate >= threeDaysAgo;
          });

          setAllTrackingData(filteredData);

          // Process data for unique users by IP
          processUniqueUsers(filteredData);

          // Process data for unique pages
          processUniquePages(filteredData);

          // Process data for daily stats graph
          processDailyStats(filteredData);
        }
      } catch (error) {
        console.error("Error fetching user tracking data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTrackingData();
  }, []);

  // Fetch non-logged-in user data from external API
  useEffect(() => {
    const fetchGuestTrackingData = async () => {
      try {
        setLoadingGuest(true);

        const limit = 20; // Records per page
        const response = await fetch(
          `https://grains.global/get_user_tracker.php?page=${guestPage}&limit=${limit}`,
          {
            headers: {
              Authorization: "Bearer AMTPL2025smTKWbApp",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.status === "success" && result.data) {
            setGuestTrackingData(result.data);
            setGuestTotal(result.total || 0);
          }
        }
      } catch (error) {
        console.error("Error fetching guest tracking data:", error);
      } finally {
        setLoadingGuest(false);
      }
    };

    fetchGuestTrackingData();
  }, [guestPage]);

  // Process unique users by IP
  const processUniqueUsers = (data) => {
    const userMap = new Map();

    data.forEach((item) => {
      const ip = item.IP || "Unknown";
      if (!userMap.has(ip)) {
        userMap.set(ip, {
          ip,
          email: item.Email || "Guest",
          visits: 0,
          lastVisit: item.CreatedAt,
        });
      }
      const user = userMap.get(ip);
      user.visits += 1;
      // Update last visit if this is more recent
      if (dayjs(item.CreatedAt).isAfter(dayjs(user.lastVisit))) {
        user.lastVisit = item.CreatedAt;
      }
    });

    const uniqueUsersList = Array.from(userMap.values()).map((user) => ({
      ...user,
      lastVisit: dayjs(user.lastVisit).format("DD MMM YYYY HH:mm"),
    }));

    setUniqueUsers(uniqueUsersList);
  };

  // Process unique pages
  const processUniquePages = (data) => {
    const pageMap = new Map();

    data.forEach((item) => {
      const page = item.Page || "Unknown";
      if (!pageMap.has(page)) {
        pageMap.set(page, {
          page,
          visits: 0,
          uniqueUsers: new Set(),
        });
      }
      const pageData = pageMap.get(page);
      pageData.visits += 1;
      if (item.IP) {
        pageData.uniqueUsers.add(item.IP);
      }
    });

    const uniquePagesList = Array.from(pageMap.values()).map((pageData) => ({
      page: pageData.page,
      visits: pageData.visits,
      uniqueUsers: pageData.uniqueUsers.size,
    }));

    // Sort by visits descending
    uniquePagesList.sort((a, b) => b.visits - a.visits);

    setUniquePages(uniquePagesList);
  };

  // Process daily stats for graph
  const processDailyStats = (data) => {
    const dailyMap = new Map();

    // Initialize last 3 days
    for (let i = 2; i >= 0; i--) {
      const date = dayjs().subtract(i, "day").format("YYYY-MM-DD");
      dailyMap.set(date, {
        date: dayjs().subtract(i, "day").format("DD MMM"),
        uniqueUsers: new Set(),
        totalVisits: 0,
      });
    }

    // Populate with data
    data.forEach((item) => {
      const date = dayjs(item.CreatedAt).format("YYYY-MM-DD");
      if (dailyMap.has(date)) {
        const dayData = dailyMap.get(date);
        dayData.totalVisits += 1;
        if (item.IP) {
          dayData.uniqueUsers.add(item.IP);
        }
      }
    });

    // Convert to array for chart
    const dailyStatsList = Array.from(dailyMap.values()).map((dayData) => ({
      date: dayData.date,
      uniqueUsers: dayData.uniqueUsers.size,
      totalVisits: dayData.totalVisits,
    }));

    setDailyStats(dailyStatsList);
  };

  // Summary cards
  const cards = [
    {
      title: "Total Unique Users",
      value: uniqueUsers.length,
      icon: <People />,
      iconColor: "#1565c0",
      bgColor: "#e3f2fd",
      textColor: "#0d47a1",
    },
    {
      title: "Total Page Visits",
      value: allTrackingData.length,
      icon: <Pageview />,
      iconColor: "#2e7d32",
      bgColor: "#e8f5e9",
      textColor: "#1b5e20",
    },
    {
      title: "Unique Pages",
      value: uniquePages.length,
      icon: <TrendingUp />,
      iconColor: "#f9a825",
      bgColor: "#fffde7",
      textColor: "#f57f17",
    },
    {
      title: "Avg Visits/User",
      value:
        uniqueUsers.length > 0
          ? (allTrackingData.length / uniqueUsers.length).toFixed(1)
          : 0,
      icon: <AccessTime />,
      iconColor: "#6a1b9a",
      bgColor: "#f3e5f5",
      textColor: "#4a148c",
    },
  ];

  // Placeholder loader
  const PlaceholderLoader = ({ height = "300px", showText = true }) => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ height, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
    >
      <div className="spinner-border text-primary mb-2" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      {showText && <p className="text-muted mb-0">Loading analytics...</p>}
    </div>
  );

  // Card placeholder
  const CardPlaceholder = () => (
    <div
      className="d-flex align-items-center shadow-sm rounded p-3 h-100"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle me-3"
        style={{
          backgroundColor: "#e9ecef",
          width: "40px",
          height: "40px",
        }}
      >
        <div
          className="spinner-border spinner-border-sm text-secondary"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
      <div>
        <div className="placeholder-glow">
          <span className="placeholder col-7 mb-1"></span>
          <span className="placeholder col-4"></span>
        </div>
      </div>
    </div>
  );

  // Function to open IP lookup in popup
  const openIPLookup = (ip) => {
    if (ip && ip !== "Unknown") {
      const url = `https://ipinfo.io/${ip}`;
      window.open(
        url,
        "IP Lookup",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );
    }
  };

  // Table columns for unique users
  const uniqueUsersColumns = [
    {
      id: "ip",
      numeric: false,
      label: "IP ADDRESS",
      renderCell: (row) => (
        <span
          onClick={() => openIPLookup(row.ip)}
          style={{
            color: row.ip !== "Unknown" ? "#1976d2" : "#666",
            cursor: row.ip !== "Unknown" ? "pointer" : "default",
            textDecoration: row.ip !== "Unknown" ? "underline" : "none",
          }}
          title={
            row.ip !== "Unknown" ? "Click to lookup IP details" : "Unknown IP"
          }
        >
          {row.ip}
        </span>
      ),
    },
    { id: "email", numeric: false, label: "EMAIL" },
    { id: "visits", numeric: true, label: "TOTAL VISITS" },
    { id: "lastVisit", numeric: false, label: "LAST VISIT" },
  ];

  // Table columns for unique pages
  const uniquePagesColumns = [
    { id: "page", numeric: false, label: "PAGE" },
    { id: "visits", numeric: true, label: "TOTAL VISITS" },
    { id: "uniqueUsers", numeric: true, label: "UNIQUE USERS" },
  ];

  // Table columns for all tracking data
  const allTrackingColumns = [
    { id: "email", numeric: false, label: "EMAIL" },
    {
      id: "ip",
      numeric: false,
      label: "IP",
      renderCell: (row) => (
        <span
          onClick={() => openIPLookup(row.ip)}
          style={{
            color: row.ip !== "Unknown" ? "#1976d2" : "#666",
            cursor: row.ip !== "Unknown" ? "pointer" : "default",
            textDecoration: row.ip !== "Unknown" ? "underline" : "none",
          }}
          title={
            row.ip !== "Unknown" ? "Click to lookup IP details" : "Unknown IP"
          }
        >
          {row.ip}
        </span>
      ),
    },
    { id: "page", numeric: false, label: "PAGE" },
    { id: "service", numeric: false, label: "SERVICE" },
    { id: "userAgent", numeric: false, label: "USER AGENT" },
    { id: "createdAt", numeric: false, label: "TIMESTAMP" },
  ];

  // Format all tracking data for table
  const formattedTrackingData = allTrackingData.map((item) => ({
    id: item.Id,
    email: item.Email || "Guest",
    ip: item.IP || "Unknown",
    page: item.Page || "N/A",
    service: item.Service || "N/A",
    userAgent: item.UserAgent ? item.UserAgent.substring(0, 50) + "..." : "N/A",
    createdAt: dayjs(item.CreatedAt).format("DD MMM YYYY HH:mm:ss"),
  }));

  // Paginated data
  const paginatedData = formattedTrackingData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <div className="buyer-dashboard">
      <div className="card">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="fw-bold">User Analytics - Last 3 Days</h4>
            <p className="text-muted">
              Track user behavior, page visits, and engagement metrics
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row g-4 mb-4">
          {loading
            ? Array.from({ length: 4 }, (_, index) => (
                <div className="col-12 col-sm-6 col-md-3" key={index}>
                  <CardPlaceholder />
                </div>
              ))
            : cards.map((card, index) => (
                <div className="col-12 col-sm-6 col-md-3" key={index}>
                  <div
                    className="d-flex align-items-center shadow-sm rounded p-3 h-100"
                    style={{ backgroundColor: card.bgColor }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle me-3"
                      style={{
                        backgroundColor: "#ffffff",
                        color: card.iconColor,
                        width: "40px",
                        height: "40px",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      {card.icon}
                    </div>
                    <div>
                      <div
                        className="text-muted"
                        style={{ fontWeight: "bold", fontSize: "16px" }}
                      >
                        {card.title}
                      </div>
                      <div
                        className="fw-bold"
                        style={{ color: card.textColor }}
                      >
                        {card.value}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {/* Daily Stats Graph */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <TrendingUp
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                Daily User Activity
              </h6>
              {loading ? (
                <PlaceholderLoader height="300px" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="uniqueUsers"
                      stroke="#1976d2"
                      strokeWidth={2}
                      name="Unique Users"
                    />
                    <Line
                      type="monotone"
                      dataKey="totalVisits"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      name="Total Visits"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Unique Users Table */}
        <div className="row mb-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <People
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                Unique Users by IP
              </h6>
              {loading ? (
                <PlaceholderLoader height="300px" />
              ) : (
                <div className="table-content">
                  <DataTable columns={uniqueUsersColumns} data={uniqueUsers} />
                </div>
              )}
            </Paper>
          </div>
        </div>

        {/* Unique Pages Table */}
        <div className="row mb-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <Pageview
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                Page Visit Statistics
              </h6>
              {loading ? (
                <PlaceholderLoader height="300px" />
              ) : (
                <div className="table-content">
                  <DataTable columns={uniquePagesColumns} data={uniquePages} />
                </div>
              )}
            </Paper>
          </div>
        </div>

        {/* All Tracking Data Table with Pagination */}
        <div className="row mb-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <AccessTime
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                All User Tracking Records
              </h6>
              {loading ? (
                <PlaceholderLoader height="400px" />
              ) : (
                <>
                  <div className="table-content">
                    <DataTable
                      columns={allTrackingColumns}
                      data={paginatedData}
                    />
                  </div>
                  {/* Custom Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span className="text-muted">
                        Showing {currentPage * rowsPerPage + 1} to{" "}
                        {Math.min(
                          (currentPage + 1) * rowsPerPage,
                          formattedTrackingData.length
                        )}{" "}
                        of {formattedTrackingData.length} records
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label className="me-2">Rows per page:</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(0);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="text-muted">
                        Page {currentPage + 1} of{" "}
                        {Math.ceil(formattedTrackingData.length / rowsPerPage)}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={
                          currentPage >=
                          Math.ceil(
                            formattedTrackingData.length / rowsPerPage
                          ) -
                            1
                        }
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Paper>
          </div>
        </div>

        {/* Non-Logged-In Users Section */}
        <div className="row mb-4 mt-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <People
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#f9a825",
                  }}
                />
                Non-Logged-In User Tracking (Guest Users)
              </h6>
              {loadingGuest ? (
                <PlaceholderLoader height="400px" />
              ) : (
                <>
                  <div className="mb-3">
                    <span className="badge bg-info">
                      Total Records: {guestTotal}
                    </span>
                  </div>
                  <div className="table-content">
                    <DataTable
                      columns={[
                        { id: "Id", numeric: true, label: "ID" },
                        { id: "Email", numeric: false, label: "EMAIL" },
                        {
                          id: "IP",
                          numeric: false,
                          label: "IP",
                          renderCell: (row) => (
                            <span
                              onClick={() =>
                                row.IP !== "Unknown" &&
                                window.open(
                                  `https://ipinfo.io/${row.IP}`,
                                  "IP Lookup",
                                  "width=800,height=600,scrollbars=yes,resizable=yes"
                                )
                              }
                              style={{
                                color:
                                  row.IP !== "Unknown" ? "#1976d2" : "#666",
                                cursor:
                                  row.IP !== "Unknown" ? "pointer" : "default",
                                textDecoration:
                                  row.IP !== "Unknown" ? "underline" : "none",
                              }}
                              title={
                                row.IP !== "Unknown"
                                  ? "Click to lookup IP details"
                                  : "Unknown IP"
                              }
                            >
                              {row.IP}
                            </span>
                          ),
                        },
                        {
                          id: "UserAgent",
                          numeric: false,
                          label: "USER AGENT",
                        },
                        { id: "Page", numeric: false, label: "PAGE" },
                        { id: "Service", numeric: false, label: "SERVICE" },
                        { id: "Reference", numeric: false, label: "REFERENCE" },
                        {
                          id: "CreatedAt",
                          numeric: false,
                          label: "TIMESTAMP",
                          renderCell: (row) =>
                            dayjs(row.CreatedAt).format("DD MMM YYYY HH:mm:ss"),
                        },
                      ]}
                      data={guestTrackingData}
                    />
                  </div>
                  {/* Pagination Info */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span className="text-muted">
                        Showing {(guestPage - 1) * 20 + 1} to{" "}
                        {Math.min(guestPage * 20, guestTotal)} of {guestTotal}{" "}
                        records
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={guestPage === 1}
                        onClick={() => setGuestPage(guestPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="text-muted">
                        Page {guestPage} of {Math.ceil(guestTotal / 20)}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={guestPage >= Math.ceil(guestTotal / 20)}
                        onClick={() => setGuestPage(guestPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmtplAdminAnalytics;
